'use client';
/**
 * LES ÉCHANGES DE PLANNING (RQ, 01/10/2026).
 * ─────────────────────────────────────────────────────────────────────────
 * Deux usages du même composant :
 *  · `moi` (« Mon planning ») — demander un échange : on choisit la date ou la
 *    plage, nos affectations apparaissent, on coche celles à céder, puis on
 *    choisit qui nous remplace parmi les personnes DÉJÀ HABILITÉES (celles déjà
 *    postées ce jour-là le sont en plus, si le RH le juge adapté).
 *    ÉCHANGE OU CESSION, au choix (RQ, 04/10/2026) : tout poste s'ÉCHANGE
 *    contre un équivalent (garde ⇄ garde, dimanche/férié ⇄ dimanche/férié,
 *    ordinaire ⇄ ordinaire, même jour compris) ; seuls une garde, un dimanche
 *    ou un férié se CÈDENT, sans contrepartie.
 *  · `rh` (« Planning ») — les demandes en attente, à valider ou refuser. Le
 *    planning ne change qu'à la validation ; les deux personnes sont notifiées.
 */
import React from 'react';
import { card, inputStyle } from '@/components/ui';
import { fetchEcriture } from '@/lib/ecrire';
import { Segment } from '@/shared/Segment';

import { Bouton } from '@/shared/Bouton';
import { jourIso, aujourdhuiIso, fmtDate } from '@/lib/meta';
import { libelleJourLong } from '@/lib/jour';
type Aff = { documentId: string; date: string; nature: string; poste: { code: string; libelle: string } | null };
type Candidat = {
  orgId: string; nom: string; absent: boolean;
  dejaPoste: { poste: string; posteLibelle: string; heureDebut?: string; heureFin?: string }[];
  contreparties: { documentId: string; date: string; poste: string; posteLibelle: string }[];
};
type Ref = { documentId: string; date: string; poste: string; posteLibelle: string };
type Echange = {
  id: string; le: string; demandeur: string; demandeurNom: string; remplacant: string; remplacantNom: string;
  type: 'cession' | 'echange'; cedees: Ref[]; reprises: Ref[]; motif?: string;
  statut: 'demandee' | 'validee' | 'refusee' | 'annulee'; decisionParNom?: string; decisionLe?: string; motifDecision?: string;
};

const fmt = (iso: string) => fmtDate(iso);
const LIB_STATUT_GESTION: Record<string, string> = { demandee: 'En attente', validee: 'Approuvée — planning à jour', refusee: 'Refusée', annulee: 'Retirée par le demandeur' };
const fmtLong = (iso: string) => libelleJourLong(iso);
const LIB_STATUT: Record<string, string> = { demandee: 'En attente du RH', validee: 'Validé — planning à jour', refusee: 'Refusé', annulee: 'Retiré' };
const quoi = (e: Echange) => `${e.cedees.map((x) => `${fmt(x.date)} ${x.posteLibelle}`).join(', ')}${e.reprises?.length ? ` ⇄ ${e.reprises.map((x) => `${fmt(x.date)} ${x.posteLibelle}`).join(', ')}` : ''}`;

/* Trois usages (04/10/2026) :
 *  · `moi`      — « Mon planning » : demander, suivre, retirer ses demandes ;
 *  · `rh`       — onglet « En cours » du planning : UNE ligne qui signale les demandes
 *                 en attente et mène à l'onglet Échanges (`onOuvrir`) ;
 *  · `gestion`  — onglet « Échanges » : tout le traitement — en attente, approbation,
 *                 refus motivé, historique filtrable. */
export function EchangesPlanning({ mode, moiKey, onChange, onOuvrir }: { mode: 'moi' | 'rh' | 'gestion'; moiKey?: string; onChange?: () => void; onOuvrir?: () => void }) {
  const [liste, setListe] = React.useState<Echange[] | null>(null);
  const [message, setMessage] = React.useState('');
  const charger = React.useCallback(async () => {
    const r = await fetch('/api/ged/ged/rh/echanges', { credentials: 'include' });
    const d = r.ok ? await r.json() : { echanges: [] };
    /* « Mes » échanges = ceux où je suis demandeur ou remplaçant. Le serveur rend
       TOUTES les demandes au rôle rh : sans ce filtre, un compte RH voyait dans
       « Mon planning » les échanges des autres comme les siens (04/10/2026). */
    const toutes = (d.echanges || []) as Echange[];
    setListe(mode === 'moi' && moiKey ? toutes.filter((e) => e.demandeur === moiKey || e.remplacant === moiKey) : toutes);
  }, [mode, moiKey]);
  // eslint-disable-next-line react-hooks/set-state-in-effect -- lecture réseau à l'ouverture
  React.useEffect(() => { void charger(); }, [charger]);

  /* ── LE FORMULAIRE (mode moi) ── */
  const [ouvert, setOuvert] = React.useState(false);
  const auj = aujourdhuiIso();
  const [du, setDu] = React.useState(auj);
  const [au, setAu] = React.useState('');
  const [miennes, setMiennes] = React.useState<Aff[] | null>(null);
  const [choisies, setChoisies] = React.useState<Set<string>>(new Set());
  const [candidats, setCandidats] = React.useState<Candidat[] | null>(null);
  const [remplacant, setRemplacant] = React.useState('');
  const [reprises, setReprises] = React.useState<Set<string>>(new Set());
  const [typeDemande, setTypeDemande] = React.useState<'echange' | 'cession'>('echange');
  /** Toutes les affectations cochées sont-elles des gardes, dimanches ou fériés ? Dit par le serveur. */
  const [cessible, setCessible] = React.useState(false);
  const [motif, setMotif] = React.useState('');
  const [envoi, setEnvoi] = React.useState(false);

  const lireMiennes = async (a: string, b: string) => {
    setMiennes(null); setChoisies(new Set()); setCandidats(null); setRemplacant(''); setReprises(new Set());
    const r = await fetch(`/api/ged/ged/rh/mon-planning?du=${a}&au=${b || a}`, { credentials: 'include' });
    const d = r.ok ? await r.json() : { affectations: [] };
    setMiennes(((d.affectations || []) as Aff[]).filter((x) => x.date >= auj && x.nature !== 'formation'));
  };
  /* Les candidats : habilités sur TOUTES les affectations cochées (intersection). */
  const lireCandidats = async (ids: Set<string>) => {
    setCandidats(null); setRemplacant(''); setReprises(new Set()); setCessible(false);
    if (!ids.size) return;
    const reponses = await Promise.all([...ids].map(async (id) => {
      const r = await fetch(`/api/ged/ged/rh/echanges/candidats?affectation=${id}`, { credentials: 'include' });
      return r.ok ? (await r.json()) as { candidats: Candidat[]; affectation?: { cessible?: boolean } } : { candidats: [] as Candidat[] };
    }));
    const tousCessibles = reponses.every((r) => r.affectation?.cessible === true);
    setCessible(tousCessibles);
    if (!tousCessibles) setTypeDemande('echange');
    const listes = reponses.map((r) => r.candidats || []);
    const [premiere, ...autres] = listes;
    setCandidats((premiere || []).filter((c) => autres.every((l) => l.some((x) => x.orgId === c.orgId)))
      .map((c) => ({ ...c, dejaPoste: listes.flatMap((l) => l.find((x) => x.orgId === c.orgId)?.dejaPoste || []) })));
  };
  const basculer = (id: string) => {
    const n = new Set(choisies); if (n.has(id)) n.delete(id); else n.add(id);
    setChoisies(n); void lireCandidats(n);
  };
  const cand = candidats?.find((c) => c.orgId === remplacant);
  const envoyer = async () => {
    setEnvoi(true); setMessage('');
    const r = await fetchEcriture('/api/ged/ged/rh/echanges', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: typeDemande, cedees: [...choisies], remplacant, reprises: typeDemande === 'echange' ? [...reprises] : [], motif }),
    });
    const d = await r.json().catch(() => ({}));
    setEnvoi(false);
    if (!r.ok) { setMessage(d?.error?.message || 'Demande refusée.'); return; }
    setOuvert(false); setMiennes(null); setChoisies(new Set()); setCandidats(null); setMotif('');
    setMessage('Demande envoyée au RH. Le planning changera à sa validation ; vous serez notifié(e), ainsi que votre remplaçant(e).');
    await charger(); onChange?.();
  };

  /* ── LA DÉCISION (mode rh) ── */
  const [refusDe, setRefusDe] = React.useState<string | null>(null);
  const [motifRefus, setMotifRefus] = React.useState('');
  const decider = async (id: string, decision: 'validee' | 'refusee', m = '') => {
    setMessage('');
    const r = await fetchEcriture(`/api/ged/ged/rh/echanges/${id}/decision`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ decision, motif: m }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setMessage(d?.error?.message || 'Décision refusée.'); return; }
    setRefusDe(null); setMotifRefus('');
    setMessage(decision === 'validee' ? 'Échange validé : le planning est à jour, les deux personnes sont notifiées.' : 'Échange refusé : le demandeur est notifié.');
    await charger(); onChange?.();
  };
  const retirer = async (id: string) => {
    const r = await fetchEcriture(`/api/ged/ged/rh/echanges/${id}/annuler`, { method: 'POST', credentials: 'include' });
    if (r.ok) await charger();
  };

  const enAttente = (liste || []).filter((e) => e.statut === 'demandee');

  /* ── GESTION : filtres et recherche ── */
  const [filtre, setFiltre] = React.useState<'demandee' | 'validee' | 'refusee' | 'annulee' | 'tous'>('demandee');
  const [recherche, setRecherche] = React.useState('');
  /* « Mon planning » montre AUSSI l'historique (RQ, 04/10/2026 : « je ne vois que les
     échanges en cours ») : toutes mes demandes, filtrables, et non plus les 12 dernières. */
  const [filtreMoi, setFiltreMoi] = React.useState<'tous' | 'demandee' | 'traitees'>('tous');

  if (mode === 'rh') {
    // Une ligne, pas un panneau : le traitement vit dans l'onglet Échanges.
    if (!enAttente.length) return null;
    return (
      <div role="status" style={{ ...card, padding: '6px 12px', margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 12.5, borderInlineStart: '3px solid var(--amber-500)' }}>
        <strong>{enAttente.length} demande{enAttente.length > 1 ? 's' : ''} d’échange en attente</strong>
        <span style={{ color: 'var(--text-muted)', flex: 1 }}>
          {enAttente.slice(0, 2).map((e) => `${e.demandeurNom} → ${e.remplacantNom}`).join(' · ')}{enAttente.length > 2 ? '…' : ''}
        </span>
        {onOuvrir && <Bouton taille="sm" type="button" onClick={onOuvrir}>Traiter dans l’onglet Échanges</Bouton>}
      </div>
    );
  }

  if (mode === 'gestion') {
    const compte = (st: string) => (liste || []).filter((e) => e.statut === st).length;
    const q = recherche.trim().toLowerCase();
    const vus = (liste || [])
      .filter((e) => filtre === 'tous' || e.statut === filtre)
      .filter((e) => !q || `${e.demandeurNom} ${e.remplacantNom} ${quoi(e)} ${e.motif || ''} ${e.id}`.toLowerCase().includes(q))
      // En attente : la plus ancienne d'abord (premier arrivé, premier traité) ; l'historique : la plus récente d'abord.
      .sort((u, w) => (filtre === 'demandee' ? u.le.localeCompare(w.le) : w.le.localeCompare(u.le)));
    const FILTRES: [typeof filtre, string, number][] = [
      ['demandee', 'En attente', compte('demandee')], ['validee', 'Approuvées', compte('validee')],
      ['refusee', 'Refusées', compte('refusee')], ['annulee', 'Retirées', compte('annulee')], ['tous', 'Toutes', (liste || []).length],
    ];
    return (
      <section aria-label="Demandes d’échange de planning" style={{ display: 'grid', gap: 10, marginTop: 12 }}>
        <div style={{ ...card, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Segment ariaLabel="Statut des demandes" valeur={filtre} onChange={setFiltre}
            options={FILTRES.map(([k, l, n]) => ({ valeur: k, libelle: l, compteur: n }))} />
          <span style={{ flex: 1 }} />
          <input type="search" value={recherche} onChange={(e) => setRecherche(e.target.value)} placeholder="Rechercher une personne, un poste…"
            aria-label="Rechercher dans les demandes" style={{ ...inputStyle, height: 30, width: 240, fontSize: 12.5 }} />
        </div>
        {message && <p role="status" style={{ fontSize: 12.5, margin: 0, color: /refus|impossible|n’est pas/i.test(message) ? 'var(--red-600)' : 'var(--green-600)' }}>{message}</p>}
        {liste === null ? <p style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Lecture des demandes…</p>
          : !vus.length ? (
            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0 }}>
              {filtre === 'demandee' && !q ? 'Aucune demande en attente.' : 'Aucune demande ne correspond.'}
            </p>
          ) : vus.map((e) => (
          <article key={e.id} style={{ ...card, padding: '10px 14px', fontSize: 12.5, borderInlineStart: `3px solid ${e.statut === 'demandee' ? 'var(--amber-500)' : e.statut === 'validee' ? 'var(--green-600)' : 'var(--border-strong)'}` }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <strong>{e.type === 'echange' ? 'Échange' : 'Cession'}</strong>
              <span>{e.demandeurNom} → {e.remplacantNom}</span>
              <span style={{ marginInlineStart: 'auto', fontWeight: 700, color: e.statut === 'validee' ? 'var(--green-600)' : e.statut === 'demandee' ? 'var(--amber-600)' : 'var(--text-muted)' }}>{LIB_STATUT_GESTION[e.statut]}</span>
            </div>
            <ul style={{ margin: '6px 0 0', paddingInlineStart: 18, lineHeight: 1.6 }}>
              {e.cedees.map((x) => <li key={`c${x.documentId}`}>{e.demandeurNom} cède le {fmtLong(x.date)} — {x.posteLibelle}</li>)}
              {(e.reprises || []).map((x) => <li key={`r${x.documentId}`}>en retour, {e.demandeurNom} prend le {fmtLong(x.date)} — {x.posteLibelle}</li>)}
            </ul>
            <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>
              Demandé le {fmtLong(jourIso(e.le))}{e.motif ? ` · motif : « ${e.motif} »` : ''}
              {e.statut !== 'demandee' && e.decisionLe ? ` · ${e.statut === 'annulee' ? 'retiré' : 'décidé'} le ${fmtLong(jourIso(e.decisionLe))}${e.decisionParNom ? ` par ${e.decisionParNom}` : ''}` : ''}
              {e.motifDecision ? ` · « ${e.motifDecision} »` : ''}
            </div>
            {e.statut === 'demandee' && (
              refusDe === e.id ? (
                <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                  <input value={motifRefus} onChange={(x) => setMotifRefus(x.target.value)} placeholder="Motif du refus — le demandeur le lira (5 caractères au moins)" style={{ ...inputStyle, flex: 1, minWidth: 260, height: 30 }} />
                  <Bouton taille="sm" type="button" onClick={() => { setRefusDe(null); setMotifRefus(''); }}>Annuler</Bouton>
                  <Bouton taille="sm" variante="danger" type="button" disabled={motifRefus.trim().length < 5} onClick={() => void decider(e.id, 'refusee', motifRefus.trim())}>Refuser</Bouton>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Bouton taille="sm" type="button" onClick={() => setRefusDe(e.id)}>Refuser…</Bouton>
                  <Bouton taille="sm" variante="primaire" type="button" onClick={() => void decider(e.id, 'validee')}>Approuver l’échange</Bouton>
                  <span style={{ color: 'var(--text-muted)' }}>Le planning change à l’approbation ; les deux personnes sont notifiées.</span>
                </div>
              )
            )}
          </article>
        ))}
      </section>
    );
  }

  return (
    <div style={{ ...card, padding: 14, margin: '0 0 14px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 13.5, flex: 1 }}>
          Mes échanges de planning
          {/* Le nombre de MES demandes en attente (comme demandeur ou comme remplaçant),
              même pastille que l'onglet Échanges du planning (RQ, 04/10/2026). */}
          {enAttente.length > 0 && (
            <span aria-label={`${enAttente.length} en attente`} title={`${enAttente.length} demande(s) en attente de la décision du RH`}
              style={{ marginInlineStart: 8, display: 'inline-block', minWidth: 18, padding: '0 6px', borderRadius: 'var(--radius-pill)', background: 'var(--amber-500)', color: 'var(--text-on-primary)', fontSize: 11, fontWeight: 700, lineHeight: '18px', textAlign: 'center', verticalAlign: 'middle' }}>
              {enAttente.length}
            </span>
          )}
        </strong>
        {mode === 'moi' && !ouvert && (
          <Bouton variante="primaire" taille="sm" type="button" onClick={() => { setOuvert(true); void lireMiennes(du, au); }}>Demander un échange</Bouton>
        )}
      </div>
      {message && <p role="status" style={{ fontSize: 12.5, margin: '6px 0 0', color: /refus|impossible|n’est pas/i.test(message) ? 'var(--red-600)' : 'var(--green-600)' }}>{message}</p>}

      {mode === 'moi' && ouvert && (
        <div style={{ marginTop: 10, padding: 12, border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)' }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ fontSize: 12.5 }}>Date<br />
              <input type="date" min={auj} value={du} onChange={(e) => { setDu(e.target.value); void lireMiennes(e.target.value, au); }} style={{ ...inputStyle, width: 160 }} />
            </label>
            <label style={{ fontSize: 12.5 }}>Jusqu’au <span style={{ color: 'var(--text-muted)' }}>(plage, facultatif)</span><br />
              <input type="date" min={du} value={au} onChange={(e) => { setAu(e.target.value); void lireMiennes(du, e.target.value); }} style={{ ...inputStyle, width: 160 }} />
            </label>
          </div>

          <div style={{ marginTop: 10, fontSize: 12.5, fontWeight: 600 }}>1. Vos affectations à céder</div>
          {miennes === null ? <p style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Lecture…</p>
            : !miennes.length ? <p style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Aucune affectation à venir sur ces dates.</p>
              : miennes.map((a) => (
                <label key={a.documentId} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, marginTop: 4 }}>
                  <input type="checkbox" checked={choisies.has(a.documentId)} onChange={() => basculer(a.documentId)} />
                  {fmt(a.date)} — {a.poste?.libelle || a.poste?.code}
                </label>
              ))}

          {choisies.size > 0 && (
            <>
              <div style={{ marginTop: 10, fontSize: 12.5, fontWeight: 600 }}>2. Type de demande</div>
              <div role="radiogroup" style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 4, fontSize: 12.5 }}>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="radio" name="type-echange" checked={typeDemande === 'echange'} onChange={() => setTypeDemande('echange')} />
                  Échange <span style={{ color: 'var(--text-muted)' }}>— vous prenez une de ses affectations en retour</span>
                </label>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', opacity: cessible ? 1 : 0.55 }}>
                  <input type="radio" name="type-echange" checked={typeDemande === 'cession'} disabled={!cessible}
                    onChange={() => { setTypeDemande('cession'); setReprises(new Set()); }} />
                  Cession <span style={{ color: 'var(--text-muted)' }}>— sans contrepartie</span>
                </label>
              </div>
              {!cessible && (
                <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '4px 0 0' }}>
                  Seuls une garde, un dimanche ou un férié se cèdent. Les autres postes s’échangent.
                </p>
              )}

              <div style={{ marginTop: 10, fontSize: 12.5, fontWeight: 600 }}>3. Qui vous remplace <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(seules les personnes déjà habilitées sont proposées)</span></div>
              {candidats === null ? <p style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Recherche des personnes habilitées…</p>
                : !candidats.length ? <p style={{ fontSize: 12.5, color: 'var(--red-600)' }}>Personne d’autre n’est habilité(e) à tous ces postes.</p>
                  : (
                    <select value={remplacant} onChange={(e) => { setRemplacant(e.target.value); setReprises(new Set()); }} style={{ ...inputStyle, maxWidth: 420 }}>
                      <option value="">— choisir —</option>
                      {candidats.map((c) => (
                        <option key={c.orgId} value={c.orgId} disabled={c.absent}>
                          {c.nom}{c.absent ? ' — absent(e)' : c.dejaPoste.length ? ` — déjà posté(e) : ${[...new Set(c.dejaPoste.map((x) => x.posteLibelle))].join(', ')}` : ''}
                        </option>
                      ))}
                    </select>
                  )}
              {cand && cand.dejaPoste.length > 0 && (
                <p style={{ fontSize: 12, color: 'var(--amber-600)', margin: '4px 0 0' }}>
                  {cand.nom} est déjà posté(e) ce jour-là : elle/il tiendra votre poste EN PLUS du sien, si le RH le juge adapté.
                </p>
              )}
              {cand && typeDemande === 'echange' && !cand.contreparties.length && (
                <p style={{ fontSize: 12.5, color: 'var(--red-600)', margin: '6px 0 0' }}>
                  {cand.nom} n’a, dans les 60 jours, aucune affectation équivalente que vous puissiez reprendre : l’échange n’est pas possible avec elle/lui.
                </p>
              )}
              {cand && typeDemande === 'echange' && cand.contreparties.length > 0 && (
                <>
                  <div style={{ marginTop: 10, fontSize: 12.5, fontWeight: 600 }}>4. Ce que vous prenez en retour <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(au moins une)</span></div>
                  {cand.contreparties.map((x) => (
                    <label key={x.documentId} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12.5, marginTop: 4 }}>
                      <input type="checkbox" checked={reprises.has(x.documentId)} onChange={() => { const n = new Set(reprises); if (n.has(x.documentId)) n.delete(x.documentId); else n.add(x.documentId); setReprises(n); }} />
                      je prends sa place le {fmt(x.date)} — {x.posteLibelle}
                    </label>
                  ))}
                </>
              )}
              <label style={{ display: 'block', marginTop: 10, fontSize: 12.5 }}>Motif <span style={{ color: 'var(--text-muted)' }}>(facultatif)</span>
                <input value={motif} onChange={(e) => setMotif(e.target.value)} style={{ ...inputStyle, width: '100%' }} />
              </label>
            </>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            {(() => {
              const pret = !!remplacant && !envoi && (typeDemande === 'cession' ? cessible : reprises.size > 0);
              return (
                <Bouton variante="primaire" type="button" disabled={!pret} onClick={() => void envoyer()}>
                  {envoi ? 'Envoi…' : typeDemande === 'echange' ? 'Demander l’échange' : 'Demander la cession'}
                </Bouton>
              );
            })()}
            <Bouton taille="sm" type="button" onClick={() => setOuvert(false)}>Annuler</Bouton>
          </div>
        </div>
      )}

      {mode === 'moi' && !!liste?.length && (
        <div style={{ marginTop: 10 }}>
          <Segment ariaLabel="Mes demandes d’échange" valeur={filtreMoi} onChange={setFiltreMoi}
            options={[
              { valeur: 'tous', libelle: 'Toutes', compteur: liste.length },
              { valeur: 'demandee', libelle: 'En attente', compteur: enAttente.length },
              { valeur: 'traitees', libelle: 'Historique', compteur: liste.length - enAttente.length },
            ]} />
        </div>
      )}
      {(liste || [])
        .filter((e) => filtreMoi === 'tous' || (filtreMoi === 'demandee' ? e.statut === 'demandee' : e.statut !== 'demandee'))
        .map((e) => (
        <div key={e.id} style={{ marginTop: 8, padding: '8px 10px', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)', fontSize: 12.5 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <strong>{e.type === 'echange' ? 'Échange' : 'Cession'}</strong>
            <span>{e.demandeurNom} → {e.remplacantNom}</span>
            <span style={{ color: 'var(--text-muted)' }}>{quoi(e)}</span>
            <span style={{ marginInlineStart: 'auto', fontWeight: 700, color: e.statut === 'validee' ? 'var(--green-600)' : e.statut === 'demandee' ? 'var(--amber-600)' : 'var(--text-muted)' }}>{LIB_STATUT[e.statut]}</span>
          </div>
          <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>
            demandé le {fmt(jourIso(e.le))}{e.decisionParNom ? ` · décidé par ${e.decisionParNom}` : ''}{e.motif ? ` · motif : ${e.motif}` : ''}{e.motifDecision ? ` · décision : ${e.motifDecision}` : ''}
          </div>
          {mode === 'moi' && e.statut === 'demandee' && e.demandeur === moiKey && (
            <Bouton taille="sm" type="button" onClick={() => void retirer(e.id)} style={{ marginTop: 6 }}>Retirer ma demande</Bouton>
          )}
        </div>
      ))}
      {mode === 'moi' && liste !== null && !liste.length && !ouvert && (
        <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '6px 0 0' }}>Aucune demande d’échange.</p>
      )}
    </div>
  );
}
